// src/taskpane/features/integrations/components/dartsip/JurisprudenceCard.tsx

import React, { useState } from 'react';
import { Calendar, Scale, MapPin, FileText, ChevronDown, ChevronUp, Download, Eye, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { JurisprudenceCase } from '../../types/integrationTypes';
import { cn } from '@/lib/utils';

interface JurisprudenceCardProps {
  case_data: JurisprudenceCase;
  onAddToKnowledgeBase: (case_id: string) => void;
  onViewDocument: (case_id: string) => void;
  isAdding?: boolean;
}

const JurisprudenceCard: React.FC<JurisprudenceCardProps> = ({ 
  case_data, 
  onAddToKnowledgeBase,
  onViewDocument,
  isAdding = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const relevanceScore = case_data.relevance_analysis?.relevance_score;
  const getRelevanceColor = (score?: number) => {
    if (!score) return 'bg-muted text-muted-foreground';
    if (score >= 0.9) return 'bg-emerald-100 text-emerald-800 font-semibold';
    if (score >= 0.8) return 'bg-blue-100 text-blue-800 font-medium';
    if (score >= 0.7) return 'bg-amber-100 text-amber-800';
    return 'bg-orange-100 text-orange-800';
  };

  const getOutcomeColor = (result: string) => {
    if (result.includes('upheld')) return 'bg-emerald-100 text-emerald-800';
    if (result.includes('dismissed')) return 'bg-rose-100 text-rose-800';
    return 'bg-muted text-muted-foreground';
  };

  // Verificar si hay documentos con URL válida
  const hasValidDocumentUrl = case_data.documents.some(
    doc => doc.url && !doc.url.includes('PLACEHOLDER')
  );

  return (
    <div className="border border-border rounded-lg p-3 bg-card hover:shadow-sm transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <h3 className="font-semibold text-sm text-foreground mb-1 leading-tight">{case_data.title}</h3>
          <div className="flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {new Date(case_data.decision_date).toLocaleDateString('en-US')}
            </span>
            <span className="flex items-center gap-1">
              <Scale className="w-3 h-3" />
              {case_data.court}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {case_data.jurisdiction}
            </span>
          </div>
        </div>
        {relevanceScore && (
          <Badge className={cn('text-[10px]', getRelevanceColor(relevanceScore))}>
            {(relevanceScore * 100).toFixed(0)}%
          </Badge>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <Badge variant="outline" className="text-[10px]">
          {case_data.procedure_type}
        </Badge>
        <Badge variant="outline" className="text-[10px]">
          {case_data.ip_type}
        </Badge>
        <Badge className={cn('text-[10px]', getOutcomeColor(case_data.outcome.result))}>
          {case_data.outcome.result.replace('_', ' ')}
        </Badge>
      </div>

      {/* AI Summary */}
      {case_data.ai_summary && (
        <div className="mb-2">
          <p className="text-xs text-foreground leading-relaxed">{case_data.ai_summary.short}</p>
        </div>
      )}

      {/* Expandable Section */}
      {isExpanded && (
        <div className="border-t border-border pt-2 mt-2 space-y-2">
          {/* Parties */}
          <div>
            <h4 className="text-[10px] font-semibold text-foreground mb-1">Parties:</h4>
            <div className="text-[10px] text-muted-foreground space-y-0.5">
              <p><span className="font-medium">Applicant:</span> {case_data.parties.applicant.join(', ')}</p>
              <p><span className="font-medium">Opponent:</span> {case_data.parties.opponent.join(', ')}</p>
            </div>
          </div>

          {/* Legal Points */}
          {case_data.legal_points && case_data.legal_points.length > 0 && (
            <div>
              <h4 className="text-[10px] font-semibold text-foreground mb-1">Legal Points:</h4>
              {case_data.legal_points.map((point, idx) => (
                <div key={idx} className="text-[10px] text-muted-foreground mb-1.5">
                  <p className="font-medium">{point.description}</p>
                  <p className="italic mt-0.5">"{point.excerpt}"</p>
                </div>
              ))}
            </div>
          )}

          {/* AI Analysis */}
          {case_data.ai_summary && (
            <div>
              <h4 className="text-[10px] font-semibold text-foreground mb-1">AI Analysis:</h4>
              <div className="text-[10px] text-muted-foreground space-y-0.5">
                <p><span className="font-medium">Practical impact:</span> {case_data.ai_summary.practical_impact}</p>
                <p><span className="font-medium">Success factor:</span> {case_data.ai_summary.success_factor}</p>
              </div>
            </div>
          )}

          {/* Documents */}
          <div>
            <h4 className="text-[10px] font-semibold text-foreground mb-1">Documents:</h4>
            <div className="space-y-1.5">
              {case_data.documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded bg-muted/50 border border-border">
                  <div className="flex items-center gap-2 flex-1">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                    <div className="text-[10px]">
                      <p className="font-medium text-foreground">{doc.type}</p>
                      <p className="text-muted-foreground">
                        {doc.language.toUpperCase()} • {doc.pages} pages • {doc.format.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  {doc.url && !doc.url.includes('PLACEHOLDER') ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDocument(case_data.case_id)}
                      className="text-[10px] h-6 px-2"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View
                    </Button>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      Pending
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[10px] h-7 px-2"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3 mr-1" />
              Less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3 mr-1" />
              More
            </>
          )}
        </Button>
        
        {/* Botón de visualización del documento */}
        {hasValidDocumentUrl && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDocument(case_data.case_id)}
            className="text-[10px] h-7 px-2"
          >
            <Eye className="w-3 h-3 mr-1" />
            View
          </Button>
        )}

        <Button
          variant="default"
          size="sm"
          onClick={() => onAddToKnowledgeBase(case_data.case_id)}
          disabled={isAdding}
          className="text-[10px] h-7 px-2 ml-auto"
        >
          <Download className="w-3 h-3 mr-1" />
          {isAdding ? 'Adding...' : 'Add to KB'}
        </Button>
      </div>
    </div>
  );
};

export default JurisprudenceCard;

